"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import PlayButton from "../ui/playbutton";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getSectionUrl, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type Props = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  className?: string;
  slug?: string;
  imageMetadata?: ImageMetadata;
};

export const VerticalBigVideoNews = ({
  category = { hidden: false, value: "LIFESTYLE", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "", customClass: "" },
  excerpt = { hidden: false, value: "", customClass: "" },
  timeAgo = { hidden: false, value: "", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  imageMetadata,
}: Props) => {
  const categoryValue = category?.value || "";
  const titleValue = title?.value || "";
  const excerptValue = excerpt?.value || "";
  const timeAgoValue = getTimeAgo(timeAgo?.value);
  const imageSrcValue = getImageUrl(imageSrc?.value) || FALLBACK_IMAGE;
  const sectionUrlValue = getSectionUrl(sectionUrl);

  const content = (
    <div className="flex flex-col bg-transparent overflow-hidden p-4 gap-4">
      {/* IMAGE */}
      {!imageSrc?.hidden && (
        <div className={imageSrc?.customClass || ""}>
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center z-10">

            </div>
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <PlayButton />
            </div>
            <Image
              src={imageSrcValue}
              alt={titleValue}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 500px"
              className="object-cover w-full h-full"
              style={getFocusPointStyle(imageMetadata)}
              loading="lazy"
              placeholder="blur"
              blurDataURL={BLUR_PLACEHOLDER}
            />
          </div>
        </div>
      )}

      {/* CONTENT */}
      <div className="flex flex-col gap-2">
        {/* Category - use span when inside a Link wrapper to avoid nested <a> tags */}
        {!category?.hidden && (
          sectionUrlValue && !slug ? (
            <Link href={sectionUrlValue} className={`text-11-tamil-600 text-FFFFFF uppercase tracking-wide hover:underline ${category?.customClass || ""}`}>
              {categoryValue}
            </Link>
          ) : (
            <span className={`text-11-tamil-600 text-FFFFFF uppercase tracking-wide ${category?.customClass || ""}`}>
              {categoryValue}
            </span>
          )
        )}

        {/* Title */}
        {!title?.hidden && (
 <h3 className={`text-24-tamil-700 md:text-16-tamil-700 text-FFFFFF leading-[1.4] whitespace-pre-line ${title?.customClass || ""}`}>
            {titleValue}
          </h3>
        )}

        {/* Description */}
        {!excerpt?.hidden && (
          <p className={`text-12-tamil-400 text-FFFFFF opacity-70 leading-[1.4] line-clamp-2 ${excerpt?.customClass || ""}`}>
            {excerptValue}
          </p>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center gap-2">
          {!timeAgo?.hidden && (
            <p className={`text-12-tamil-500 text-FFFFFF ${timeAgo?.customClass || ""}`}>{timeAgoValue}</p>
          )}
          <div className="flex gap-3">
            <MessageIcon
              id="small-image-news-vert-message-icon"
              variant="message-icon-4"
              slug={slug}
              category={BOOKMARK_CATEGORIES.VIDEOS}
              title={titleValue}
              thumbnailUrl={imageSrcValue}
              description={excerptValue}
              articleUrl={slug ? `/${slug}` : undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (slug) {
    return (
      <Link href={`/${slug}`} className="block hover:opacity-90 transition-opacity cursor-pointer">
        {content}
      </Link>
    );
  }

  return content;
};